/**
 * The 3D model's labels and jaw, on the measured anatomy (anatomy.gen.ts) and the tongue targets
 * (tongueTargets.ts). Units are mm with the origin at the upper incisors' cutting edge; x to the
 * speaker's left, y up, z forward.
 */
import type { PoseId, ProfilePoint } from './articulation';
import { AIRWAY_WALL, ROOF } from './tongueTargets';

/** Jaw joint (temporomandibular): the lower jaw opens about it. */
export const JAW_PIVOT: readonly [number, number, number] = [0, 45, -88];

/** A point on the lower jaw after the jaw opens by `degrees` about JAW_PIVOT. */
export function openJaw(
  [x, y, z]: readonly [number, number, number],
  degrees: number,
): [number, number, number] {
  const a = (degrees * Math.PI) / 180;
  const [, py, pz] = JAW_PIVOT;
  const dy = y - py;
  const dz = z - pz;
  return [x, py + dy * Math.cos(a) - dz * Math.sin(a), pz + dy * Math.sin(a) + dz * Math.cos(a)];
}

/** Where a label points: a part of the tongue's midline profile, its highest point, or the lips. */
export type MarkAnchor =
  'tip' | 'back' | 'highest' | 'root' | 'rootLow' | 'lips' | 'roof' | 'throat';

export interface PoseMark {
  text: string;
  at: MarkAnchor;
  /** Where the words go relative to the point, so they don't cover what it shows. */
  place?: 'above' | 'below' | 'front';
}

/**
 * The labels that point at what matters in each pose (the visual review found the key feature
 * unreadable without them). Short, concrete words a child can follow.
 */
export const POSE_MARKS: Readonly<Record<PoseId, readonly PoseMark[]>> = {
  rest: [],
  rBunched: [
    { text: 'middle up', at: 'highest', place: 'below' },
    { text: 'tip down', at: 'tip', place: 'front' },
    { text: 'root back', at: 'root', place: 'below' },
  ],
  rRetroflex: [
    { text: 'tip up', at: 'tip', place: 'front' },
    { text: 'root back', at: 'rootLow', place: 'front' },
  ],
  w: [
    { text: 'back up', at: 'back' },
    { text: 'lips round', at: 'lips', place: 'front' },
  ],
  l: [{ text: 'tip touches', at: 'tip', place: 'front' }],
};

/** On the lips' front (where they round and push out). */
const LIPS_POINT: readonly [number, number, number] = [0, -2, 16];

/**
 * The point (mm, [x, y, z]) a label points at, on the tongue's current midline dorsum (tip to
 * root) or the anatomy.
 */
export function markPoint(
  dorsum: readonly ProfilePoint[],
  at: MarkAnchor,
): [number, number, number] {
  if (at === 'lips') return [...LIPS_POINT];
  if (at === 'roof') {
    const [z, y] = ROOF.find(([z]) => z <= -30) ?? [-30, 22];
    return [0, y, z];
  }
  if (at === 'throat') {
    const [z, y] = AIRWAY_WALL.find(([, y]) => y <= 30) ?? [-81, 30];
    return [0, y, z];
  }
  const pick = (score: (p: ProfilePoint) => number) =>
    dorsum.reduce((best, p) => (score(p) > score(best) ? p : best), dorsum[0] ?? [0, 0]);
  let point: ProfilePoint;
  if (at === 'tip') point = dorsum[0] ?? [0, 0];
  else if (at === 'highest') point = pick(([z, y]) => (z > -62 ? y : -Infinity));
  else if (at === 'back') point = pick(([z, y]) => (z < -50 && z > -68 ? y : -Infinity));
  else point = pick(([z, y]) => (y > -15 ? -z : -Infinity)); // root: the back-most point
  return [0, point[1], point[0]];
}

/** Names of the landmarks, shown faintly with every pose so the lines read as anatomy. */
export const STRUCTURE_MARKS: readonly PoseMark[] = [
  { text: 'roof of mouth', at: 'roof', place: 'above' },
  { text: 'throat', at: 'throat', place: 'above' },
];
